import { useRouter } from "next/router"
import { useEffect, useState, useRef } from "react"
import {
  abandonSwedbankCheckout,
  getSwedbankPayOrderScript
} from "@/lib/api/checkoutForm"
import { useScript } from "@/lib/hooks/useScript"
import { useAppContext, usePropsObject } from "@/lib/contexts/appContext"
import { usePostType } from "@/lib/helpers/template"
import { themeBgColor } from "@/lib/helpers/utils"
import swedbankPayStyles from "@/components/checkout-form/payment/gateways/swedbankPayStyles"
import Steps from "@/components/checkout-form/steps"
import { CheckoutProvider } from "@/components/checkout-form/store"
import { Spinner } from "@/components/common/loading/spinner"
import styles from "./checkout-form.module.css"
import Block from "@/components/wrappers/block"

export const SWEDBANK_PAY_CONTAINER_ID = "swedbank-pay-seamless-view-page"

function CheckoutForm({ block, tracking }) {
  const { checkoutFormRef } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [userCancelPayment, setUserCancelPayment] = useState(false)
  const abandonSyncRef = useRef(false)
  const prevOrderKeyRef = useRef(undefined)
  const router = useRouter()
  const postType = usePostType()
  const { theme } = usePropsObject()
  const scriptStatus = useScript(result?.script)

  useEffect(() => {
    const orderKeyFromQuery = router.query?.key

    if (orderKeyFromQuery !== prevOrderKeyRef.current) {
      prevOrderKeyRef.current = orderKeyFromQuery
      abandonSyncRef.current = false
    }
  }, [router.query?.key])

  useEffect(() => {
    if (!router.query?.key) {
      return
    }

    setLoading(true)

    if (router.query?.swp_utold) {
      setUserCancelPayment(true)
      setLoading(false)

      if (!abandonSyncRef.current) {
        abandonSyncRef.current = true
        abandonSwedbankCheckout(router.query.key).catch(() => {})
      }
      return
    }

    getSwedbankPayOrderScript(router.query.key)
      .then((res) => {
        if (!res?.script || !Number.isFinite(Number(res.orderId))) {
          setUserCancelPayment(true)
          setLoading(false)
          return
        }
        setResult(res)
      })
      .catch(() => {
        setLoading(false)
        router.replace("/?error=getSwedbankPayOrderScript")
      })
  }, [router])

  useEffect(() => {
    if (scriptStatus !== "ready" || !result?.orderId || !router.query?.key) {
      return
    }

    const swedbankPaySettings = {
      container: SWEDBANK_PAY_CONTAINER_ID,
      style: swedbankPayStyles,
      culture: "sv-SE",
      onPaid: () => {
        router.replace(
          `/kassa/order-received/${result.orderId}?key=${router.query.key}`
        )
      },
      onAborted: () => {
        setUserCancelPayment(true)
        setResult(null)

        if (!abandonSyncRef.current) {
          abandonSyncRef.current = true
          abandonSwedbankCheckout(router.query.key).catch(() => {})
        }
      }
    }

    try {
      window.payex.hostedView.checkout(swedbankPaySettings).open()
    } catch (error) {
      router.replace("/?error=" + encodeURIComponent(JSON.stringify(error)))
    }
  }, [scriptStatus, router, result])

  const wrapperClasses = []

  if (postType === "campaign") {
    wrapperClasses.push(themeBgColor(theme?.bgColor))
    wrapperClasses.push("text-black-100 pt-13")
  }

  if (theme?.accentColor) {
    wrapperClasses.push(styles.campaign, `theme-accent-${theme.accentColor}`)
  }

  return (
    <Block blockRef={checkoutFormRef} width="column">
      {loading && !userCancelPayment && (
        <div className="fixed inset-0 flex items-center justify-center w-screen h-screen bg-white z-100">
          <Spinner sizeClasses="w-16 h-16 border-8" />
        </div>
      )}

      {result && <div id={SWEDBANK_PAY_CONTAINER_ID} />}

      {!loading && !result && !userCancelPayment && (
        <CheckoutProvider form={block.attrs}>
          <Steps tracking={tracking} form={block.attrs} />
        </CheckoutProvider>
      )}
      {userCancelPayment && (
        <div className="fixed inset-0 flex items-center justify-center w-screen h-screen bg-white z-100 flex-col text-center p-2">
          <h2 className="mb-4">Du valde att avbryta din betalning</h2>
          <p>
            Stäng denna flik för att komma tillbaka till Naturskyddsföreningen.
          </p>
        </div>
      )}
    </Block>
  )
}

export default CheckoutForm
